import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Phone, MessageCircle, ArrowUpRight, Send, Loader2, CheckCircle2, BadgeCheck } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { WA_NUMBER } from "../data/site";

const EMAIL = process.env.REACT_APP_CONTACT_EMAIL || "";
const PHONE = process.env.REACT_APP_CONTACT_PHONE || "";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADDRESS = "Astra Towers, Rajarhat, New Town, Kolkata – 700161";
const MAPS_URL = "https://www.google.com/maps/search/?api=1&query=Astra+Towers+Rajarhat+New+Town+Kolkata+700161";

const WHY = [
  "ISO 9001:2015 certified — one accountable partner from design to support",
  "Network, security, AV, data centre, cloud and sourcing under one roof",
  "SLA-driven support with named owners on every engagement",
];

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); setErrors({ ...errors, [k]: "" }); };

  const goForm = () => document.getElementById("enquiry-form")?.scrollIntoView({ behavior: "smooth" });

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email is required";
    if (form.phone && !/^[+\d][\d\s-]{7,14}$/.test(form.phone)) e.phone = "Enter a valid phone number";
    if (!form.message.trim()) e.message = "Please share your requirement";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, interest: "General Enquiry" });
      setSent(true);
    } catch {
      toast.error("Could not submit right now. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const cards = [
    { id: "office", icon: MapPin, title: "Office", value: "Astra Towers, Rajarhat", sub: "New Town, Kolkata – 700161", btn: "Get Directions", href: MAPS_URL },
    { id: "email", icon: Mail, title: "Email", value: EMAIL || "Shared on request", sub: EMAIL ? "Official enquiry email" : "Official email being configured", btn: "Write to Us", href: EMAIL ? `mailto:${EMAIL}` : null },
    { id: "phone", icon: Phone, title: "Phone", sub: PHONE ? "Official line" : "Official number being configured", value: PHONE ? PHONE.replace(/^\+91(\d{5})(\d{5})$/, "+91 $1 $2") : "Shared on request", btn: "Call Now", href: PHONE ? `tel:${PHONE}` : null },
    { id: "whatsapp", icon: MessageCircle, title: "WhatsApp", value: WA_NUMBER ? `+${WA_NUMBER.replace(/(\d{2})(\d{5})(\d{5})/, "$1 $2 $3")}` : "Shared on request", sub: "Fastest way to reach us", btn: "Chat With Us", href: WA_NUMBER ? `https://wa.me/${WA_NUMBER}` : null },
  ];

  const inputCls = (k) =>
    `w-full rounded-xl border px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors bg-white/[0.04] ${
      errors[k] ? "border-red-500/60" : "border-white/10 focus:border-smipl-orange"
    }`;

  return (
    <main className="pt-36 pb-28 min-h-screen" data-testid="contact-page">
      <div className="max-w-6xl mx-auto px-6">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-smipl-orange">Contact SMIPL</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            Let's Build <span className="text-smipl-orange">What's Next.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-8 max-w-3xl space-y-4 text-base md:text-lg text-slate-400 leading-relaxed">
            <p>
              SMIPL is a technology solutions and system integration company based in <span className="text-white font-medium">Kolkata, India</span>.
              Since 2022, we have been helping businesses and organisations design, deploy and manage reliable technology infrastructure.
            </p>
            <p>
              From network infrastructure and cybersecurity to data centres, enterprise communication, audio-visual solutions,
              cloud infrastructure and IT equipment, SMIPL brings technology, expertise and execution together under one roof.
            </p>
            <p>
              Whether you are planning a new technology deployment, upgrading existing infrastructure or looking for a trusted
              technology partner, SMIPL is here to help.
            </p>
          </div>
        </Reveal>

        <section className="mt-24" data-testid="reach-us">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-smipl-orange mb-3">Reach Us</p>
            <h2 className="font-display text-2xl md:text-4xl font-bold tracking-tight mb-10">Connect With Our Team</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {cards.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.07}>
                <div data-testid={`contact-card-${c.id}`} className="tracing-border group h-full rounded-2xl border border-white/8 bg-navy-deep/60 p-7 flex flex-col transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-smipl-orange/10 border border-smipl-orange/25 text-smipl-orange group-hover:bg-smipl-orange group-hover:text-white transition-all duration-300">
                    <c.icon size={19} />
                  </div>
                  <h3 className="mt-6 font-mono text-[11px] tracking-[0.25em] uppercase text-slate-500">{c.title}</h3>
                  <p className="mt-2 font-display text-base font-bold text-white leading-snug">{c.value}</p>
                  <p className="mt-1 text-xs text-slate-500">{c.sub}</p>
                  {c.href ? (
                    <a
                      data-testid={`contact-card-${c.id}-btn`}
                      href={c.href}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-auto pt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-smipl-orange hover:text-smipl-amber transition-colors"
                    >
                      {c.btn} <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </a>
                  ) : (
                    <button
                      data-testid={`contact-card-${c.id}-btn`}
                      onClick={goForm}
                      className="mt-auto pt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-smipl-orange hover:text-smipl-amber transition-colors text-left"
                    >
                      {c.btn} <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </button>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="mt-24" data-testid="map-section">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-smipl-orange mb-3">Visit Us</p>
            <h2 className="font-display text-2xl md:text-4xl font-bold tracking-tight">Find Us on the Map</h2>
            <p className="mt-3 flex items-center gap-2 text-sm text-slate-400">
              <MapPin size={15} className="text-smipl-orange" /> {ADDRESS}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 rounded-3xl overflow-hidden border border-white/10" data-testid="office-map">
              <iframe
                title="SMIPL Office — Astra Towers, Rajarhat, New Town, Kolkata"
                src="https://maps.google.com/maps?q=Astra%20Towers%20Rajarhat%20New%20Town%20Kolkata%20700161&z=14&output=embed"
                className="w-full h-72 md:h-96 grayscale-[0.4] contrast-[1.05]"
                loading="lazy"
              />
            </div>
          </Reveal>
        </section>

        <section id="enquiry-form" className="mt-24" data-testid="enquiry-section">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div>
              <Reveal>
                <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-smipl-orange mb-3">Enquiry</p>
                <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Tell Us What You Need</h2>
                <p className="mt-4 text-slate-400 text-base leading-relaxed">
                  Have a technology requirement, infrastructure project or upcoming deployment? Share your requirements
                  with us and our team will get back to you.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-10 mb-4 font-mono text-[11px] tracking-[0.3em] uppercase text-slate-500">Why SMIPL</p>
                <ul className="space-y-3.5">
                  {WHY.map((w) => (
                    <li key={w} className="flex items-start gap-3 text-sm text-slate-300">
                      <BadgeCheck size={16} className="mt-0.5 shrink-0 text-smipl-orange" /> {w}
                    </li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={0.18}>
                <div className="mt-10 rounded-2xl border border-smipl-orange/25 bg-smipl-orange/[0.06] p-6" data-testid="prefer-to-talk">
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-smipl-orange">Prefer to talk?</p>
                  <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                    Chat with us on WhatsApp at{" "}
                    <a href={WA_NUMBER ? `https://wa.me/${WA_NUMBER}` : "#enquiry-form"} className="text-white font-semibold hover:text-smipl-orange transition-colors">
                      +91 98363 72200
                    </a>
                    {EMAIL ? (
                      <> or write to <a href={`mailto:${EMAIL}`} className="text-white font-semibold hover:text-smipl-orange transition-colors">{EMAIL}</a></>
                    ) : (
                      <> — or send the form and we'll call you back</>
                    )}
                    .
                  </p>
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.12}>
              <div className="rounded-3xl border border-white/12 bg-white/[0.03] p-8 md:p-9">
                {sent ? (
                  <div data-testid="enquiry-success" className="flex items-start gap-4 py-6">
                    <CheckCircle2 size={24} className="text-wa shrink-0 mt-0.5" />
                    <div>
                      <p className="font-display text-lg font-bold text-white">Enquiry submitted successfully.</p>
                      <p className="mt-1 text-sm text-slate-300">Our team will get back to you shortly. Thank you for reaching out to SMIPL.</p>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-4" noValidate>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <input data-testid="enquiry-name" className={inputCls("name")} placeholder="Name *" value={form.name} onChange={set("name")} />
                        {errors.name && <p className="mt-1.5 text-xs text-red-400">{errors.name}</p>}
                      </div>
                      <div>
                        <input data-testid="enquiry-company" className={inputCls("company")} placeholder="Company" value={form.company} onChange={set("company")} />
                      </div>
                      <div>
                        <input data-testid="enquiry-email" className={inputCls("email")} placeholder="Email *" value={form.email} onChange={set("email")} />
                        {errors.email && <p className="mt-1.5 text-xs text-red-400">{errors.email}</p>}
                      </div>
                      <div>
                        <input data-testid="enquiry-phone" className={inputCls("phone")} placeholder="Phone" value={form.phone} onChange={set("phone")} />
                        {errors.phone && <p className="mt-1.5 text-xs text-red-400">{errors.phone}</p>}
                      </div>
                    </div>
                    <div>
                      <textarea
                        data-testid="enquiry-message"
                        rows={5}
                        className={`${inputCls("message")} resize-none`}
                        placeholder="Requirement / Message — goals, timelines, anything else we should know *"
                        value={form.message}
                        onChange={set("message")}
                      />
                      {errors.message && <p className="mt-1.5 text-xs text-red-400">{errors.message}</p>}
                    </div>
                    <button
                      data-testid="enquiry-submit-btn"
                      disabled={sending}
                      className="w-full flex items-center justify-center gap-2 rounded-full bg-smipl-orange text-white py-3.5 text-sm font-semibold hover:bg-smipl-amber hover:glow-orange disabled:opacity-60 transition-all duration-300"
                    >
                      {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                      {sending ? "Submitting…" : "Submit Enquiry"}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    </main>
  );
};

export default ContactPage;
